import { createSelector, createSlice } from '@reduxjs/toolkit';

import type { DefaultMapSettingsDto } from 'cadenza/api-client/curated-map-views-api';
import {
  getAllCuratedMapViews,
  getCuratedDefaultMap,
  isDefaultClassicMapDefined as fetchIsDefaultClassicMapDefined,
} from 'cadenza/api-client/curated-map-views-api';
import { createAsyncThunk, createSliceFunction, getStore } from 'cadenza/store/cadenza-store';
import type { CuratedMapViewDto } from 'cadenza/api-client/model/curated-map-view-dto';

export const SLICE_PREFIX = 'defaultMapSlice';

declare module 'cadenza/store/cadenza-store' {
  interface RootState {
    [SLICE_PREFIX]: DefaultMapState;
  }
}

const slice = createSliceFunction(SLICE_PREFIX);

interface DefaultMapState {
  defaultClassicMapDefined: boolean;
  defaultMapSettings: DefaultMapSettingsDto;
  curatedMapViews: CuratedMapViewDto[];
}

function createInitialState(): DefaultMapState {
  return {
    defaultClassicMapDefined: true,
    defaultMapSettings: {
      validity: 'NONE',
    },
    curatedMapViews: [],
  };
}

export const fetchInitialDefaultMapState = createAsyncThunk(SLICE_PREFIX + '/fetchInitialDefaultMapState', () =>
  fetchState(),
);

export const refreshCuratedMapViews = createAsyncThunk(SLICE_PREFIX + '/refreshCuratedMapViews', () =>
  getAllCuratedMapViews().then((curatedMapsTableData) => curatedMapsTableData.rows),
);

export const defaultMapSlice = createSlice({
  name: SLICE_PREFIX,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchInitialDefaultMapState.fulfilled, (state, action) => {
        const newState: DefaultMapState = action.payload;
        Object.assign(state, newState);
      })
      .addCase(refreshCuratedMapViews.fulfilled, (state, action) => {
        state.curatedMapViews = action.payload;
      }),
});

async function fetchState() {
  const [defaultClassicMapDefined, defaultMapSettings, curatedMapViews] = await Promise.all([
    fetchIsDefaultClassicMapDefined(),
    getCuratedDefaultMap(),
    getAllCuratedMapViews(),
  ]);
  return { defaultClassicMapDefined, defaultMapSettings, curatedMapViews: curatedMapViews.rows };
}

const { reducer } = defaultMapSlice;
getStore().registerReducer(SLICE_PREFIX, reducer);

const isAnyCuratedMapViewDefined = createSelector(slice, (state) => state.curatedMapViews.length > 0);
const isDefaultClassicMapDefined = createSelector(slice, (state) => state.defaultClassicMapDefined);

export const isDefaultCuratedMapDefined = createSelector(slice, (state) => {
  const defaultMapSettings = state.defaultMapSettings;
  return defaultMapSettings.itemId !== undefined && defaultMapSettings.validity === 'VALID';
});

export const isAnyDefaultMapDefined = createSelector(
  [isDefaultClassicMapDefined, isDefaultCuratedMapDefined],
  (classicDefined, curatedDefined) => classicDefined || curatedDefined,
);

export const isAnyDefaultMapViewAvailable = createSelector(
  [isAnyDefaultMapDefined, isAnyCuratedMapViewDefined],
  (anyDefault, anyCurated) => anyDefault || anyCurated,
);

export const noDefaultMapIsDefined = createSelector(isAnyDefaultMapDefined, (a) => !a);

export const noDefaultMapIsAvailable = createSelector(isAnyDefaultMapViewAvailable, (a) => !a);

export const getCuratedMapViews = createSelector(slice, (state) => state.curatedMapViews);

export const getOnlyCuratedMapView = createSelector(getCuratedMapViews, (curatedMapViews) => {
  if (curatedMapViews.length === 1) {
    return curatedMapViews[0];
  }
});
